import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Play, X } from 'lucide-react';
import { modalOverlay, modalContent } from '../motion/presets';
import '../styles/ProjectModal.css';

export default function ProjectModal({ project, language, t, onClose }) {
    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === 'Escape') onClose();
        },
        [onClose]
    );

    useEffect(() => {
        if (project) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [project, handleKeyDown]);

    if (!project) return null;

    const d = project.details;
    const isTr = language === 'tr';
    const mt = t.projects.modal;

    const sections = [
        { label: mt.problem, content: isTr ? d.problemTr : d.problemEn },
        { label: mt.role, content: isTr ? d.roleTr : d.roleEn },
        { label: mt.approach, content: isTr ? d.approachTr : d.approachEn },
        {
            label: mt.highlights,
            list: isTr ? d.highlightsTr : d.highlightsEn,
        },
        { label: mt.results, content: isTr ? d.resultsTr : d.resultsEn },
    ];

    return (
        <AnimatePresence>
            {project && (
                <motion.div
                    className="modal-overlay"
                    variants={modalOverlay}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    onClick={onClose}
                >
                    <motion.div
                        className="modal-content"
                        variants={modalContent}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label={isTr ? project.titleTr : project.titleEn}
                    >
                        <button
                            className="modal-close"
                            onClick={onClose}
                            aria-label={mt.close}
                        >
                            <X size={20} />
                        </button>

                        <div className="modal-header">
                            <div className="modal-tags">
                                {project.tags.map((tag) => (
                                    <span key={tag} className="modal-tag">{tag}</span>
                                ))}
                            </div>
                            <h2 className="modal-title">
                                {isTr ? project.titleTr : project.titleEn}
                            </h2>
                            {(project.videoLink || project.websiteLink) && (
                                <div className="modal-links">
                                    {project.videoLink && (
                                        <a
                                            href={project.videoLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="modal-link"
                                        >
                                            <Play size={15} />
                                            <span>{t.projects.watchVideo}</span>
                                        </a>
                                    )}
                                    {project.websiteLink && (
                                        <a
                                            href={project.websiteLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="modal-link"
                                        >
                                            <Globe size={15} />
                                            <span>{t.projects.viewWebsite}</span>
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="modal-body">
                            {sections.map((section) => (
                                <div key={section.label} className="modal-section">
                                    <h3 className="modal-section__label">{section.label}</h3>
                                    {section.content && (
                                        <p className="modal-section__text">{section.content}</p>
                                    )}
                                    {section.list && (
                                        <ul className="modal-section__list">
                                            {section.list.map((item, i) => (
                                                <li key={i} className="modal-section__list-item">
                                                    <span className="modal-section__bullet" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
